import { useState, useCallback } from 'react'
import {
	FileText,
	Plus,
	Pencil,
	Trash2,
	Loader2,
	GripVertical,
	PanelRightClose,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { ScrollArea } from '@/common/ui/scroll-area'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'

import {
	useQueryChatNotes,
	useMutationCreateChatNote,
	useMutationUpdateChatNote,
	useMutationDeleteChatNote,
	useQueryChatRoom,
} from '../../../api/hooks'
import { PlayerStatsPanel } from './PlayerStatsPanel'
import { PlayerCumulativePanel } from './PlayerCumulativePanel'

import type { components } from '@/schema'

type ChatNoteResponse = components['schemas']['ChatNoteResponse']

const noteFormSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกหัวข้อ'),
	content: z.string().min(1, 'กรุณากรอกเนื้อหา'),
})

type NoteFormValues = z.infer<typeof noteFormSchema>

const MIN_PANEL_WIDTH = 280
const MAX_PANEL_WIDTH = 500
const DEFAULT_PANEL_WIDTH = 320

interface ChatNotesPanelProps {
	roomId: string
	onClose: () => void
}

export function ChatNotesPanel({ roomId, onClose }: ChatNotesPanelProps) {
	const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list')
	const [editingNote, setEditingNote] = useState<ChatNoteResponse | null>(null)
	const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null)
	const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH)
	const [isDragging, setIsDragging] = useState(false)

	const { data: notesData, isLoading } = useQueryChatNotes(roomId, true)
	const { data: room } = useQueryChatRoom(roomId)
	const createNoteMutation = useMutationCreateChatNote(roomId)
	const updateNoteMutation = useMutationUpdateChatNote(roomId)
	const deleteNoteMutation = useMutationDeleteChatNote(roomId)

	const form = useForm<NoteFormValues>({
		resolver: zodResolver(noteFormSchema),
		defaultValues: {
			title: '',
			content: '',
		},
	})

	const handleCreateNote = () => {
		form.reset({ title: '', content: '' })
		setMode('create')
	}

	const handleEditNote = (note: ChatNoteResponse) => {
		setEditingNote(note)
		form.reset({ title: note.title, content: note.content })
		setMode('edit')
	}

	const handleDeleteNote = (noteId: string) => {
		setDeleteNoteId(noteId)
	}

	const confirmDelete = async () => {
		if (deleteNoteId) {
			await deleteNoteMutation.mutateAsync(deleteNoteId)
			setDeleteNoteId(null)
		}
	}

	const handleSubmit = async (values: NoteFormValues) => {
		if (mode === 'create') {
			await createNoteMutation.mutateAsync(values)
		} else if (mode === 'edit' && editingNote) {
			await updateNoteMutation.mutateAsync({
				noteId: editingNote.id,
				data: values,
			})
		}
		setMode('list')
		setEditingNote(null)
		form.reset()
	}

	const handleCancel = () => {
		setMode('list')
		setEditingNote(null)
		form.reset()
	}

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			setIsDragging(true)

			const startX = e.clientX
			const startWidth = panelWidth

			const handleMouseMove = (e: MouseEvent) => {
				// Moving left increases width (panel is on right side)
				const deltaX = startX - e.clientX
				const newWidth = Math.max(
					MIN_PANEL_WIDTH,
					Math.min(MAX_PANEL_WIDTH, startWidth + deltaX),
				)
				setPanelWidth(newWidth)
			}

			const handleMouseUp = () => {
				setIsDragging(false)
				document.removeEventListener('mousemove', handleMouseMove)
				document.removeEventListener('mouseup', handleMouseUp)
			}

			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
		},
		[panelWidth],
	)

	const notes = notesData?.items || []

	return (
		<>
			<div className={cn('flex h-full', isDragging && 'select-none')}>
				{/* Resize Handle */}
				<div
					onMouseDown={handleMouseDown}
					className={cn(
						'relative flex w-1 shrink-0 cursor-col-resize items-center justify-center bg-border transition-colors hover:bg-primary/20',
						'after:absolute after:inset-y-0 after:left-1/2 after:w-3 after:-translate-x-1/2',
						isDragging && 'bg-primary/30',
					)}
				>
					<div className="z-10 flex h-8 w-4 items-center justify-center rounded-sm border bg-background shadow-sm hover:bg-muted">
						<GripVertical className="h-4 w-4 text-muted-foreground" />
					</div>
				</div>

				{/* Panel Content */}
				<div
					className="flex h-full flex-col border-l bg-background"
					style={{ width: panelWidth }}
				>
					{/* Header */}
					<div className="flex items-center justify-between border-b px-4 py-3">
						<div className="flex items-center gap-2">
							<FileText className="h-5 w-5 text-muted-foreground" />
							<h3 className="font-semibold">
								{mode === 'list' && 'รายละเอียด'}
								{mode === 'create' && 'สร้างโน้ตใหม่'}
								{mode === 'edit' && 'แก้ไขโน้ต'}
							</h3>
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8"
							onClick={onClose}
						>
							<PanelRightClose className="h-4 w-4" />
						</Button>
					</div>

					{/* Content */}
					<div className="flex-1 overflow-y-auto">
						{mode === 'list' ? (
							<div className="flex h-full flex-col">
								{/* Player Stats Section */}
								<div className="border-b px-4 py-2">
									<PlayerStatsPanel
										roomId={roomId}
										hasPlayer={!!room?.player}
									/>
								</div>

								{/* Player Cumulative Section */}
								{room?.player?.id && (
									<div className="border-b px-4 py-2">
										<PlayerCumulativePanel playerId={room.player.id} />
									</div>
								)}

								{/* Notes Section */}
								<div className="flex flex-1 flex-col p-4">
									<Button
										onClick={handleCreateNote}
										className="mb-4 w-full"
										variant="outline"
									>
										<Plus className="mr-2 h-4 w-4" />
										สร้างโน้ตใหม่
									</Button>

									{isLoading ? (
										<div className="flex flex-1 items-center justify-center">
											<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
										</div>
									) : notes.length === 0 ? (
										<div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
											<FileText className="mb-2 h-12 w-12 opacity-50" />
											<p>ยังไม่มีโน้ต</p>
											<p className="text-sm">
												คลิกปุ่มด้านบนเพื่อสร้างโน้ตใหม่
											</p>
										</div>
									) : (
										<ScrollArea className="flex-1">
											<div className="space-y-3 pr-2">
												{notes.map((note) => (
													<div
														key={note.id}
														className="group w-full rounded-lg border bg-card p-3 transition-colors hover:bg-accent/50"
													>
														<div className="mb-2 flex items-start justify-between">
															<h4 className="text-sm font-semibold leading-tight">
																{note.title}
															</h4>
															<div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-6 w-6"
																	onClick={() => handleEditNote(note)}
																>
																	<Pencil className="h-3 w-3" />
																</Button>
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-6 w-6 text-destructive hover:text-destructive"
																	onClick={() => handleDeleteNote(note.id)}
																>
																	<Trash2 className="h-3 w-3" />
																</Button>
															</div>
														</div>
														<p className="mb-2 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
															{note.content}
														</p>
														<div className="flex items-center justify-between text-[10px] text-muted-foreground">
															<span>
																{note.lastUpdatedBy?.username || 'ไม่ทราบ'}
															</span>
															<span>
																{formatDistanceToNow(new Date(note.updatedAt), {
																	addSuffix: true,
																	locale: th,
																})}
															</span>
														</div>
													</div>
												))}
											</div>
										</ScrollArea>
									)}
								</div>
							</div>
						) : (
							<div className="p-4">
								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(handleSubmit)}
										className="space-y-4"
									>
										<FormField
											control={form.control}
											name="title"
											render={({ field }) => (
												<FormItem>
													<FormLabel>หัวข้อ</FormLabel>
													<FormControl>
														<Input placeholder="หัวข้อโน้ต" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="content"
											render={({ field }) => (
												<FormItem>
													<FormLabel>เนื้อหา</FormLabel>
													<FormControl>
														<Textarea
															placeholder="เนื้อหาโน้ต..."
															className="min-h-[120px] resize-none"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<div className="flex justify-end gap-2">
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={handleCancel}
											>
												ยกเลิก
											</Button>
											<Button
												type="submit"
												size="sm"
												disabled={
													createNoteMutation.isPending ||
													updateNoteMutation.isPending
												}
											>
												{(createNoteMutation.isPending ||
													updateNoteMutation.isPending) && (
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												)}
												{mode === 'create' ? 'สร้าง' : 'บันทึก'}
											</Button>
										</div>
									</form>
								</Form>
							</div>
						)}
					</div>
				</div>
			</div>

			<AlertDialog
				open={!!deleteNoteId}
				onOpenChange={(open) => !open && setDeleteNoteId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการลบโน้ต</AlertDialogTitle>
						<AlertDialogDescription>
							คุณแน่ใจหรือไม่ว่าต้องการลบโน้ตนี้?
							การดำเนินการนี้ไม่สามารถย้อนกลับได้
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{deleteNoteMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							ลบ
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
